import { ShieldCheck, Handshake, FileText, Wrench, Award, Users } from "lucide-react";
import { Link } from "react-router-dom";

const TRUST_IMG = "https://images.unsplash.com/photo-1567789884554-0b844b597180?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function About() {
  return (
    <div data-testid="about-page">
      <section className="border-b border-slate-200 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-12 md:py-20">
          <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 mb-4 animate-fade-up">
            <span className="divider-thin" />Über D.R Automobile
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-6xl tracking-tight text-slate-900 max-w-3xl leading-[1.05] animate-fade-up delay-100">
            Inhabergeführt. Persönlich. <span className="font-serif italic font-medium">Seriös.</span>
          </h1>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-16 md:py-24 grid md:grid-cols-2 gap-12 md:gap-20 items-center">
        <div className="aspect-[4/5] overflow-hidden border border-slate-200">
          <img src={TRUST_IMG} alt="D.R Automobile — Standort Vöcklabruck" className="w-full h-full object-cover" />
        </div>
        <div>
          <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight text-slate-900 leading-tight">
            Eine Hand. Ein Wort. Ein Auto, dem Sie vertrauen können.
          </h2>
          <p className="mt-6 text-slate-700 leading-relaxed">
            D.R Automobile ist ein Einzelbetrieb mit Sitz in Österreich. Als Inhaber kümmere ich
            mich persönlich um jeden Schritt — von der Auswahl der Fahrzeuge bis zur Schlüsselübergabe.
            Das bedeutet kurze Wege, klare Absprachen und volle Verantwortung.
          </p>
          <p className="mt-4 text-slate-700 leading-relaxed">
            Sie kaufen kein Auto „von einem Händler". Sie kaufen es von mir. Und ich stehe für jedes
            Fahrzeug, das mein Lager verlässt.
          </p>
        </div>
      </section>

      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-28">
          <div className="text-[10px] uppercase tracking-[0.25em] text-red-500 mb-3">So arbeite ich</div>
          <h2 className="font-heading font-bold text-3xl md:text-5xl tracking-tight max-w-3xl mb-16">
            Drei Prinzipien — kompromisslos.
          </h2>
          <div className="grid md:grid-cols-3 gap-10 md:gap-6 md:divide-x divide-slate-800">
            {[
              { icon: ShieldCheck, title: "Qualität vor Quantität", text: "Lieber wenige, dafür sorgfältig geprüfte Fahrzeuge. Jedes Auto wird vor Aufnahme in den Bestand technisch begutachtet." },
              { icon: Handshake, title: "Persönlich & direkt", text: "Sie sprechen immer mit mir. Keine Hotline, kein Wechsel, kein Weiterleiten." },
              { icon: FileText, title: "Volle Transparenz", text: "Serviceheft, Pickerl, Vorbesitzer, Schäden — alle Informationen offen vor dem Kauf." },
            ].map((p, i) => (
              <div key={i} className="md:px-8 first:md:pl-0 last:md:pr-0">
                <p.icon size={32} strokeWidth={1.4} className="mb-5 text-red-500" />
                <h3 className="font-heading font-semibold text-xl mb-3">{p.title}</h3>
                <p className="text-slate-300 leading-relaxed text-sm">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 md:py-28">
        <div className="grid md:grid-cols-3 gap-10">
          <Stat icon={Award} number="2026" label="Gegründet in Vöcklabruck, Oberösterreich" />
          <Stat icon={Users} number="1:1" label="Persönliche Beratung — direkt vom Inhaber" />
          <Stat icon={Wrench} number="100%" label="Geprüfte Fahrzeuge vor Übergabe" />
        </div>
      </section>

      <section className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-20 text-center">
          <h2 className="font-heading font-bold text-3xl md:text-4xl tracking-tight text-slate-900">Lernen Sie uns kennen.</h2>
          <p className="text-slate-600 mt-3 max-w-xl mx-auto">Ein Anruf, eine E-Mail oder ein Besuch — ich freue mich auf das Gespräch.</p>
          <div className="mt-8 flex justify-center gap-4 flex-wrap">
            <Link to="/fahrzeuge" data-testid="about-cta-inventory" className="bg-slate-900 text-white px-8 py-3.5 text-sm font-medium hover:bg-red-600 transition-crisp">Fahrzeuge ansehen</Link>
            <Link to="/kontakt" data-testid="about-cta-contact" className="border border-slate-900 px-8 py-3.5 text-sm font-medium hover:bg-slate-900 hover:text-white transition-crisp">Kontakt aufnehmen</Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ icon: Icon, number, label }) {
  return (
    <div className="border-l-2 border-slate-900 pl-6">
      <Icon size={28} strokeWidth={1.4} className="text-slate-900 mb-4" />
      <div className="font-heading font-bold text-4xl md:text-5xl text-slate-900">{number}</div>
      <div className="text-slate-600 text-sm mt-2">{label}</div>
    </div>
  );
}
